import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import VideoModal from "@/components/VideoModal";
import VideoCard from "@/components/VideoCard";
import EventModal from "@/components/EventModal";
import {
  Pyramid, Sun, Star, Compass, Scroll, Brain, Globe, Eye, Moon,
  ArrowRight, Calendar, BookOpen, GraduationCap, Film, Sparkles, Lightbulb, Leaf, Search, Headphones, Play, MessagesSquare, Quote,
} from "lucide-react";
import { CATEGORY_IMG, READING_IMG, MOVIE_IMGS } from "@/lib/assets";
import { AudiobookCover } from "@/pages/Audiobooks";
import Newsletter from "@/components/Newsletter";
import Ticker from "@/components/Ticker";

const ICONS = { Pyramid, Sun, Star, Compass, Scroll, Brain, Globe, Eye, Moon, Leaf };
const HERO = "https://images.pexels.com/photos/30766684/pexels-photo-30766684.png";

export default function Home() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [events, setEvents] = useState([]);
  const [readings, setReadings] = useState([]);
  const [classes, setClasses] = useState([]);
  const [movies, setMovies] = useState([]);
  const [insights, setInsights] = useState([]);
  const [audiobooks, setAudiobooks] = useState([]);
  const [voices, setVoices] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);
  const [eventOpen, setEventOpen] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/videos?limit=8").then((r) => setFeatured(r.data.slice(0, 8)));
    api.get("/events").then((r) => setEvents(r.data));
    api.get("/readings").then((r) => setReadings(r.data));
    api.get("/classes").then((r) => setClasses(r.data));
    api.get("/movies").then((r) => setMovies(r.data));
    api.get("/insights").then((r) => setInsights(r.data));
    api.get("/audiobooks").then((r) => setAudiobooks(r.data)).catch(() => {});
    api.get("/forum/recent-replies?limit=6").then((r) => setVoices(r.data)).catch(() => {});
  }, []);

  const play = (v) => { setActive(v); setOpen(true); };

  const runSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/videos?search=${encodeURIComponent(q.trim())}`);
    else navigate("/videos");
  };

  // Headline rotates every 12 hours, cycling through events ordered most-current-first
  const sortedEvents = [...events].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const bucket = sortedEvents.length ? Math.floor(Date.now() / (12 * 3600 * 1000)) % sortedEvents.length : 0;
  const headline = sortedEvents[bucket] || null;

  const openEvent = (ev) => {
    setActiveEvent(ev);
    setEventOpen(true);
    api.post(`/events/${ev.id}/access`).catch(() => {});
  };

  // Rotating audiobook teaser (featured & most-played first, cycles every 12h)
  const abPool = [...audiobooks].sort((a, b) => (Number(b.featured) - Number(a.featured)) || ((b.plays || 0) - (a.plays || 0)));
  const abBucket = abPool.length > 4 ? Math.floor(Date.now() / (12 * 3600 * 1000)) % abPool.length : 0;
  const abTeaser = abPool.length ? Array.from({ length: Math.min(4, abPool.length) }, (_, i) => abPool[(abBucket + i) % abPool.length]) : [];

  return (
    <main className="bg-onyx" data-testid="home-page">
      {/* TICKER — sits directly below the fixed banner, in normal flow so it never covers content */}
      <div className="pt-16"><Ticker /></div>

      {/* HERO */}
      <section className="relative min-h-[calc(100vh-6.5rem)] flex items-center grain">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-24 pb-16 w-full">
          <div className="max-w-3xl animate-fade-up">
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-gold mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-gold" /> For Africa & Her Diaspora
            </p>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] text-bone mb-8">
              Awaken the <span className="text-gold italic">Ancient</span> Knowledge Within
            </h1>
            <p className="text-lg md:text-xl text-bone/70 max-w-2xl leading-relaxed mb-10">
              A digital sanctuary of esoteric, spiritual, scientific and metaphysical teachings — from the masters of our people. No prior knowledge required. Begin exactly where you are.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/videos" className="bg-gold text-black px-8 py-3.5 font-medium hover:bg-gold2 transition-colors flex items-center gap-2" data-testid="hero-explore-btn">
                Explore the Teachings <ArrowRight size={18} />
              </Link>
              <form onSubmit={runSearch} className="flex items-stretch bg-black/40 border border-white/15 focus-within:border-gold/60 transition-colors" data-testid="hero-search-form">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the site..." aria-label="Search the site"
                  data-testid="hero-search-input"
                  className="bg-transparent px-4 py-3.5 text-bone placeholder:text-bone/40 focus:outline-none w-44 sm:w-56" />
                <button type="submit" className="px-4 bg-white/5 hover:bg-gold hover:text-black text-gold transition-colors flex items-center justify-center" data-testid="hero-search-btn" aria-label="Search">
                  <Search size={18} />
                </button>
              </form>
              <Link to="/classes" className="border border-white/20 text-bone px-8 py-3.5 font-medium hover:bg-white/5 hover:border-gold/40 transition-colors" data-testid="hero-classes-btn">
                Enroll in a Class
              </Link>
            </div>
            <div className="mt-14 flex flex-wrap gap-10 text-bone/60">
              <div><div className="font-serif text-3xl text-gold">10</div><div className="text-sm">Sacred Categories</div></div>
              <div><div className="font-serif text-3xl text-gold">330+</div><div className="text-sm">Curated Lectures</div></div>
              <div><div className="font-serif text-3xl text-gold">6</div><div className="text-sm">Guided Classes</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24" data-testid="categories-section">
        <SectionHead over="The Ten Pillars" title="Explore by Category" sub="Ten major streams of knowledge. Tap any to enter the archive of lectures." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {cats.map((c, i) => {
            const Icon = ICONS[c.icon] || Sparkles;
            return (
              <Link key={c.slug} to={`/videos?category=${c.slug}`} data-testid={`category-card-${c.slug}`}
                className="group relative bg-surface border border-white/10 hover:border-gold/50 overflow-hidden transition-all animate-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}>
                <img src={CATEGORY_IMG[c.slug]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-onyx via-onyx/80 to-onyx/40" />
                <div className="relative p-8">
                  <Icon className="text-gold mb-5 group-hover:scale-110 transition-transform" size={34} strokeWidth={1.5} />
                  <h3 className="font-serif text-2xl text-bone mb-2 leading-tight">{c.name}</h3>
                  <p className="text-bone/60 text-sm leading-relaxed mb-5">{c.blurb}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono text-gold/90">{c.video_count} lectures</span>
                    <ArrowRight className="text-bone/40 group-hover:text-gold group-hover:translate-x-1 transition-all" size={18} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED VIDEOS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16" data-testid="featured-section">
        <div className="flex items-end justify-between mb-10">
          <SectionHead over="Featured" title="Latest Teachings" noMargin />
          <Link to="/videos" className="text-gold text-sm flex items-center gap-2 hover:gap-3 transition-all">View all <ArrowRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {featured.map((v) => <VideoCard key={v.id} video={v} onPlay={play} />)}
        </div>
      </section>

      {/* CURRENT EVENTS */}
      <section className="bg-surface border-y border-white/10 py-24 grain" data-testid="events-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <SectionHead over="Current Relative Events" title="What's Happening Now" icon={Calendar} noMargin />
            <Link to="/archive" className="text-gold text-sm flex items-center gap-2 hover:gap-3 transition-all" data-testid="events-archive-link">
              Article Archive <ArrowRight size={16} />
            </Link>
          </div>

          {headline && (
            <button onClick={() => openEvent(headline)} data-testid="headline-event"
              className="group relative w-full text-left overflow-hidden border border-gold/30 bg-gradient-to-br from-black to-surface2 mb-8 hover:border-gold/60 transition-colors min-h-[340px] flex items-end">
              {headline.image_url && (
                <img src={headline.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
              <div className="relative p-8 md:p-12 w-full">
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-black bg-gold px-3 py-1 mb-5">
                  <Sparkles size={13} /> Headline Event
                </span>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs uppercase tracking-widest text-gold border border-gold/30 px-2 py-0.5">{headline.tag}</span>
                  <span className="text-bone/50 text-sm font-mono">{headline.date}</span>
                </div>
                <h3 className="font-serif text-3xl md:text-5xl text-bone leading-tight mb-4 max-w-4xl group-hover:text-gold transition-colors">{headline.title}</h3>
                <p className="text-bone/70 text-lg max-w-3xl mb-4">{headline.summary}</p>
                <span className="text-gold text-sm flex items-center gap-2">Read the full article <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
                <p className="mt-5 text-bone/40 text-xs font-mono">Headline rotates every 12 hours to feature the most current event.</p>
              </div>
            </button>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.filter((e) => !headline || e.id !== headline.id).map((e) => (
              <button key={e.id} onClick={() => openEvent(e)}
                className="group bg-surface border border-white/10 text-left hover:border-gold/40 transition-colors flex flex-col overflow-hidden" data-testid="event-card">
                {e.image_url && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={e.image_url} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs uppercase tracking-widest text-black bg-gold px-2.5 py-0.5">{e.tag}</span>
                    <span className="text-bone/40 text-xs font-mono">{e.date}</span>
                  </div>
                  <h3 className="font-serif text-xl text-bone mb-2 leading-snug">{e.title}</h3>
                  <p className="text-bone/55 text-sm leading-relaxed flex-1">{e.summary}</p>
                  <span className="mt-4 text-gold text-xs flex items-center gap-1.5">Read more <ArrowRight size={13} /></span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* IN-SIGHT SPOTLIGHT */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24" data-testid="insight-section">
        <SectionHead over="Spotlight" title="In-Sight" sub="Concepts for Internal Consciousness Development." icon={Lightbulb} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {insights.map((it, i) => (
            <div key={it.id} className="group relative bg-surface border border-white/10 hover:border-gold/50 p-8 transition-colors animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid="insight-card">
              <div className="flex items-start justify-between mb-5">
                <span className="font-serif text-5xl text-gold/30 leading-none">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-xs uppercase tracking-widest text-gold border border-gold/30 px-3 py-1">{it.concept}</span>
              </div>
              <h3 className="font-serif text-2xl text-bone mb-3 leading-tight">{it.title}</h3>
              <p className="text-bone/60 leading-relaxed">{it.description}</p>
              {it.recommended_reading && (
                <div className="mt-5 pt-4 border-t border-white/10 flex items-start gap-2" data-testid="insight-reading">
                  <BookOpen size={15} className="text-gold/70 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-gold/70">Recommended Reading</p>
                    <p className="text-bone/70 text-sm mt-0.5">{it.recommended_reading}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>


      {/* RECOMMENDED READING */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24" data-testid="reading-section">
        <SectionHead over="Recommended Reading" title="Foundational Texts" icon={BookOpen} />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5 mt-12">
          {readings.map((b) => (
            <div key={b.id} className="group" data-testid="reading-card">
              <div className={`relative aspect-[3/4] bg-gradient-to-br ${b.image_color} border border-white/10 flex items-end p-4 group-hover:border-gold/50 transition-colors overflow-hidden`}>
                <img src={READING_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
                <h4 className="font-serif text-lg text-white leading-tight relative z-10">{b.title}</h4>
              </div>
              <p className="text-bone/50 text-sm mt-3">{b.author}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CLASSES */}
      <section className="bg-surface border-y border-white/10 py-24" data-testid="classes-preview-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between mb-10">
            <SectionHead over="Guided Learning" title="Classes & Training" icon={GraduationCap} noMargin />
            <Link to="/classes" className="text-gold text-sm flex items-center gap-2 hover:gap-3 transition-all">All classes <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {classes.slice(0, 3).map((c) => (
              <div key={c.id} className="bg-black/60 backdrop-blur-xl border border-white/10 p-8 hover:border-gold/40 transition-colors" data-testid="class-preview-card">
                <p className="text-xs uppercase tracking-widest text-gold mb-3">{c.organization}</p>
                <h3 className="font-serif text-2xl text-bone mb-4">{c.title}</h3>
                <p className="text-bone/55 text-sm leading-relaxed mb-6">{c.description}</p>
                <div className="flex items-center gap-4 text-xs text-bone/40 font-mono">
                  <span>{c.level}</span><span>·</span><span>{c.lessons} lessons</span><span>·</span><span>{c.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MOVIES & TRAINING */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24" data-testid="movies-section">
        <SectionHead over="Screening Room" title="Movies & Documentaries" icon={Film} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {movies.map((m, i) => (
            <div key={m.id} className={`relative aspect-video bg-gradient-to-br ${m.image_color} border border-white/10 p-6 flex flex-col justify-end group hover:border-gold/50 transition-colors overflow-hidden`} data-testid="movie-card">
              <img src={MOVIE_IMGS[i % MOVIE_IMGS.length]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="relative">
                <span className="text-xs uppercase tracking-widest text-gold mb-2 block">{m.type} · {m.year}</span>
                <h3 className="font-serif text-2xl text-white leading-tight">{m.title}</h3>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* AUDIOBOOK LIBRARY TEASER */}
      {abTeaser.length > 0 && (
        <section className="bg-surface border-y border-white/10 py-24" data-testid="audiobook-teaser-section">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="flex items-end justify-between mb-10">
              <SectionHead over="Listen & Awaken" title="From the Audiobook Library" icon={Headphones} noMargin />
              <Link to="/audiobooks" className="text-gold text-sm flex items-center gap-2 hover:gap-3 transition-all" data-testid="audiobook-teaser-all">All audiobooks <ArrowRight size={16} /></Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {abTeaser.map((b) => (
                <Link key={b.id} to={`/audiobooks/${b.id}`} className="group block" data-testid="audiobook-teaser-card">
                  <AudiobookCover book={b} className="aspect-[3/4] w-full" />
                  <h3 className="font-serif text-lg text-bone mt-3 leading-snug group-hover:text-gold transition-colors line-clamp-2">{b.title}</h3>
                  <p className="text-bone/50 text-sm mt-0.5">{b.author}</p>
                  <p className="text-bone/30 text-xs mt-1 flex items-center gap-2">
                    <span>{b.source_label || "Listen"}</span>
                    {b.plays > 0 && <span className="flex items-center gap-1"><Play size={10} /> {b.plays}</span>}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RECENT VOICES FROM THE FORUM */}
      {voices.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-24" data-testid="recent-voices-section">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <SectionHead over="The Reasoning Circle" title="Recent Voices from the Circle" icon={MessagesSquare} noMargin />
            <Link to="/forum" className="text-gold text-sm flex items-center gap-2 hover:gap-3 transition-all" data-testid="recent-voices-all">Join the conversation <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {voices.map((v) => (
              <Link key={v.id} to={`/forum/${v.topic_id}`} data-testid="recent-voice-card"
                className="group relative bg-surface border border-white/10 hover:border-gold/50 transition-colors p-6 flex flex-col">
                <Quote size={20} className="text-gold/40 mb-3 shrink-0" />
                <p className="text-bone/75 text-sm leading-relaxed line-clamp-4 flex-1">{v.body}</p>
                <div className="mt-5 pt-4 border-t border-white/10">
                  <p className="text-bone font-medium text-sm">{v.author_name}</p>
                  <p className="text-gold/80 text-xs mt-1 group-hover:text-gold transition-colors line-clamp-1">on “{v.topic_title}”</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Newsletter />

      <VideoModal video={active} open={open} onClose={setOpen} />
      <EventModal event={activeEvent} open={eventOpen} onClose={setEventOpen} />
    </main>
  );
}

function SectionHead({ over, title, sub, icon: Icon, noMargin }) {
  return (
    <div className={noMargin ? "" : "max-w-2xl"}>
      <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4 flex items-center gap-2">
        {Icon && <Icon size={16} />} {over}
      </p>
      <h2 className="font-serif text-4xl md:text-5xl text-bone leading-tight">{title}</h2>
      {sub && <p className="text-bone/55 mt-4 text-lg">{sub}</p>}
    </div>
  );
}
